import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  InputLabel,
  Box,
  Typography,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useIRGrade } from "./hooks";
import { toast as toastFn } from "react-toastify";
// import { GRADES } from './constants';
import Loader from "../../components/Loader";
import { uploadJson } from "../../store/IRGrading/IRGradingBaseApi";
import { useCourse } from "../course/hooks";
import { useGrade } from "../gradingModel/hooks";
import { DiagnosticT1 } from "./DiagnosticT1";
import { DiagnosticT2 } from "./DiagnosticT2";
import { generateUUID, json_files } from "../../utils";
import { course as courseConst } from "../course/constants";

export const Edit = () => {
  const {
    create,
    save,
    loadAllGrades,
    grades,
    loadCourseMapping,
    courseMappingList,
    loadGradeMap,
    gradingList,
    loadCurrent,
    loading,
    loadCourseMappingV2,
    courseMappingListV2,
  } = useIRGrade();
  const { loadCoursesList, diagnosticList, lastPage } = useCourse();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tempGrading, setTempGrading] = useState();
  const [irGrading, setIrGrading] = useState();
  const [diagnosticT1, setDiagnosticT1] = useState({});
  const [diagnosticT2, setDiagnosticT2] = useState([]);
  const [applicableGrades, setApplicableGrades] = useState([]);
  const [nextLevelAssessments, setNextLevelAssessments] = useState([]);
  const [isMapped, setIsMapped] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [studentType, setStudentType] = useState("URBAN");

  useEffect(() => {
    // loadGrades();
    loadGradeMap();
    // loadCourseMapping();
    loadCourseMappingV2();
    loadAllGrades({});
  }, []);

  useEffect(() => {
    if (id) {
      loadCurrent({ id });
    }
  }, [id]);

  useEffect(() => {
    setTempGrading(gradingList);
    if (id) {
      let tempGradeList = gradingList.find(
        (grading) => grading.irGradingId === id,
      );
      setIrGrading(tempGradeList);
      setDiagnosticT1(tempGradeList?.diagnosticOne);
      setDiagnosticT2(tempGradeList?.diagnosticTwo);
      setApplicableGrades(tempGradeList?.diagnosticOne?.applicableGrades);
      setStudentType(
        (tempGradeList?.studentType || "URBAN").toUpperCase(),
      );
      setIsMapped(true);
      // setIsSubmit(true);
    } else if (!id) {
      setDiagnosticT1({
        courseId: "",
        gradingModel: "",
        applicableGrades: [],
        nextLevelAssessments: [],
      });
      setDiagnosticT2([]);
      setApplicableGrades([]);
      setIrGrading({});
      setStudentType("URBAN");
      setIsMapped(false);
      setIsSubmit(false);
    }
  }, [gradingList, id]);

  useEffect(() => {
    loadCoursesList({ pageSize: 50, category: "Diagnostic" });
  }, []);

  const isGradeAlreadyMapped = (gradeId, currentGradingId) => {
    // A grade collides only when it's mapped within the SAME studentType.
    // Same grade with a different studentType (URBAN vs RURAL) is allowed.
    return tempGrading.some(
      (grading) =>
        grading.irGradingId !== currentGradingId &&
        (grading.studentType || "URBAN").toUpperCase() === studentType &&
        grading.diagnosticOne?.applicableGrades?.some(
          (grade) => grade.id === gradeId,
        ),
    );
  };

  const handleChipClick = (item) => {
    // Toggle grade selection

    const isDuplicate = isGradeAlreadyMapped(item.id, id);

    if (isDuplicate) {
      toastFn.error(`${item.grade} is already mapped.`);
      return;
    }

    // Allow only one selection
    setApplicableGrades([{ id: item.id, grade: item.grade }]);

    // Update `irGrading` state to reflect changes in `applicableGrades`
    setDiagnosticT1((prevState) => ({
      ...prevState,
      applicableGrades: [{ id: item.id, grade: item.grade }],
    }));
  };

  const handleChipDelete = (item) => {
    setApplicableGrades([]);
    setDiagnosticT1((prevState) => ({
      ...prevState,
      applicableGrades: [],
    }));
  };

  const mapTest1 = () => {
    const duplicateGrade = applicableGrades.find((grade) =>
      isGradeAlreadyMapped(grade.id, id),
    );

    if (duplicateGrade) {
      toastFn.error(`${duplicateGrade.grade} is already mapped.`);
      return;
    }

    setDiagnosticT1((prevState) => ({
      ...prevState,
      applicableGrades: applicableGrades || [],
      nextLevelAssessments: nextLevelAssessments || [],
    }));
    setIsMapped(true);
    toastFn.success("Diagnostic Test 1 Mapped Successfully");
  };

  const handleSubmit = async (e) => {
    if (!(diagnosticT1?.applicableGrades.length > 0)) {
      return toastFn.warn("Please select atleast one Grade");
    }
    const irGradingId = generateUUID();
    let data = {
      irGradingId,
      studentType,
      diagnosticOne: diagnosticT1,
      diagnosticTwo: diagnosticT2,
    };

    let updatedData = [...tempGrading];

    if (id) {
      let tempData = tempGrading.map((item) => {
        if (id === item.irGradingId) {
          return data;
        } else {
          return item;
        }
      });
      updatedData = tempData;
    } else {
      updatedData.push(data);
    }

    const json = JSON.stringify(updatedData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const formData = new FormData();
    formData.append("file", blob, json_files.gradingModels.name);
    try {
      const resp = await uploadJson({ id, formData });

      if (resp.message) {
        toastFn.success(`IR Grading Update Success`);
        navigate("/ir-grading");
      }
    } catch (e) {
      return toastFn(e.error, "error");
    }

    if (id) {
      data = { ...irGrading, id };
      save({ grade: data, navigate });
    } else {
      create({ grade: data, navigate });
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box
          sx={{
            padding: "0px 20px",
            [theme.breakpoints.down("md")]: {
              padding: "0px",
            },
          }}
        >
          <div className="question header-container">
            <div className="header">
              <Typography
                align="left"
                variant="h1"
                color="primary.blue"
                sx={{
                  margin: "0",
                  fontWeight: "700",
                  lineHeight: "1.5",
                  fontSize: "2rem",
                }}
              >
                {"Diagnostic Test Mapping"}
              </Typography>
            </div>
            <Grid
              sx={styles.gridContainer}
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputLabel style={styles.inputLabel}>
                  Student Type <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    name="studentType"
                    value={studentType}
                    onChange={(e) => {
                      setStudentType(e.target.value);
                      // If the currently-selected grade is already mapped under
                      // the new studentType, clear it so the admin re-picks.
                      if (
                        applicableGrades?.length > 0 &&
                        tempGrading?.some(
                          (g) =>
                            g.irGradingId !== id &&
                            (g.studentType || "URBAN").toUpperCase() ===
                              e.target.value &&
                            g.diagnosticOne?.applicableGrades?.some(
                              (x) => x.id === applicableGrades[0].id,
                            ),
                        )
                      ) {
                        toastFn.info(
                          "Selected grade is already mapped under " +
                            e.target.value +
                            " — clearing.",
                        );
                        setApplicableGrades([]);
                        setDiagnosticT1((prev) => ({
                          ...prev,
                          applicableGrades: [],
                        }));
                      }
                    }}
                    sx={{ height: "47px" }}
                  >
                    {Object.keys(courseConst.STUDENT_TYPE).map((key) => (
                      <MenuItem key={key} value={key}>
                        {courseConst.STUDENT_TYPE[key]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <InputLabel style={styles.inputLabel}>Select Grade</InputLabel>
                <Box
                  sx={{
                    border: "1px solid grey",
                    padding: 2,
                    borderRadius: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {grades?.data?.map((item) => {
                    const isSelected = applicableGrades?.some(
                      (grade) => grade.id === item.id,
                    );

                    return (
                      <Chip
                        key={item.id}
                        label={item.grade}
                        onClick={() => handleChipClick(item)}
                        onDelete={
                          isSelected ? () => handleChipDelete(item) : undefined
                        }
                        deleteIcon={isSelected}
                        disabled={!isSelected && applicableGrades?.length > 0}
                        sx={{
                          backgroundColor: isSelected ? "#403e75" : "grey",
                          color: "white",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          "& .MuiChip-deleteIcon": {
                            fontSize: "16px",
                            color: "var(--mui-palette-error-light)",
                            "&:hover": {
                              color: "var(--mui-palette-error-main)",
                            },
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
            <Grid
              sx={styles.gridContainer}
              container
              spacing={2}
              alignItems={"flex-start"}
            >
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <DiagnosticT1
                  courseList={(diagnosticList || []).filter(
                    (c) =>
                      (c.studentType || "URBAN").toUpperCase() === studentType,
                  )}
                  diagnosticT1={diagnosticT1}
                  setDiagnosticT1={setDiagnosticT1}
                  handleSubmit={mapTest1}
                  isMapped={isMapped}
                  setIsMapped={setIsMapped}
                  diagnosticT2={diagnosticT2}
                  setDiagnosticT2={setDiagnosticT2}
                  nextLevelAssessments={nextLevelAssessments}
                  setNextLevelAssessments={setNextLevelAssessments}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <DiagnosticT2
                  courseList={(diagnosticList || []).filter(
                    (c) =>
                      (c.studentType || "URBAN").toUpperCase() === studentType,
                  )}
                  diagnosticT1={diagnosticT1}
                  diagnosticT2={diagnosticT2}
                  setDiagnosticT2={setDiagnosticT2}
                  isMapped={isMapped}
                  isSubmit={isSubmit}
                  setIsSubmit={setIsSubmit}
                />
              </Grid>
            </Grid>
            <Button
              sx={{
                mt: 1,
              }}
              variant="contained"
              disabled={!isSubmit || !diagnosticT2?.length > 0}
              onClick={handleSubmit}
            >
              {!id ? "Create" : "Save Changes"}
            </Button>
          </div>
        </Box>
      )}
    </>
  );
};

const styles = {
  label: {
    color: "var(--mui-palette-common-black)",
    fontWeight: "bold",
  },
  inputLabel: {
    color: "var(--mui-palette-primary-darkGrey)",
    marginBottom: "5px",
    fontWeight: "600",
    fontSize: "1em",
  },
  gridContainer: {
    mt: 1,
    display: "flex",
  },
  suggestionBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "10px",
  },
  suggestionItem: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: "2px",
    cursor: "pointer",
    boxShadow: "0 4px 8px var(--mui-palette-secondary-boxShadow1)",
    backgroundColor: "var(--mui-palette-gray-lightGrey300)",
  },
  uploadBtn: {
    width: "30px",
    height: "30px",
    boxShadow: "none",
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: "0",
    minWidth: "30px",
  },
  hiddenInput: {
    display: "none",
  },
  actionBtnItem: {
    marginLeft: "5px",
    marginRight: "5px",
    cursor: "pointer",
    display: "flex",
    gap: "20px",
    alignItems: "center",
  },
  row: {
    padding: "5px 16px",
  },
};
